import { createAxiosInstance } from '../utils/axios.js'
import axios from 'axios'
import { API_BASE_URL } from '../config.js'

const api = createAxiosInstance()
const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
})

export const adminService = {
  async getBooks (params = {}) {
    const response = await api.get('/api/admin/books', { params })
    return response.data
  },

  async getBook (id) {
    const response = await api.get(`/api/admin/books/${id}`)
    return response.data
  },

  async createBook (book) {
    const response = await api.post('/api/admin/books', book)
    return response.data
  },

  async updateBook (id, book) {
    const response = await api.put(`/api/admin/books/${id}`, book)
    return response.data
  },

  async deleteBook (id) {
    await api.delete(`/api/admin/books/${id}`)
  },

  // Undo a soft delete: the book returns to the storefront with its purchase
  // history and digital entitlements intact (they were never removed).
  async restoreBook (id) {
    await api.post(`/api/admin/books/${id}/restore`)
  },

  async getDiscountCodes (page = 1, limit = 100) {
    const response = await api.get('/api/admin/discounts', {
      params: { page, limit }
    })
    return response.data
  },

  async getDiscountCode (id) {
    const response = await api.get(`/api/admin/discounts/${id}`)
    return response.data
  },

  async createDiscountCode (code) {
    const response = await api.post('/api/admin/discounts', code)
    return response.data
  },

  async updateDiscountCode (id, code) {
    const response = await api.put(`/api/admin/discounts/${id}`, code)
    return response.data
  },

  async deleteDiscountCode (id) {
    await api.delete(`/api/admin/discounts/${id}`)
  },

  async getEvents (page = 1, limit = 10) {
    const response = await api.get('/api/admin/events', {
      params: { page, limit }
    })
    return response.data
  },

  async getEvent (id) {
    const response = await api.get(`/api/admin/events/${id}`)
    return response.data
  },

  async getTickets (
    page = 1,
    limit = 10,
    isUsed = null,
    transactionId = null,
    ticketCode = null,
    eventId = null
  ) {
    const params = { page, limit }
    if (isUsed !== null) {
      params.is_used = isUsed
    }
    if (transactionId) {
      params.transaction_id = transactionId
    }
    if (ticketCode) {
      params.ticket_code = ticketCode
    }
    if (eventId) {
      params.event_id = eventId
    }
    const response = await api.get('/api/admin/tickets', { params })
    return response.data
  },

  async getValidTickets (page = 1, limit = 10, eventId = null) {
    const params = { page, limit }
    if (eventId) {
      params.event_id = eventId
    }
    const response = await api.get('/api/admin/tickets/valid', { params })
    return response.data
  },

  async getUsedTickets (page = 1, limit = 10, eventId = null) {
    const params = { page, limit }
    if (eventId) {
      params.event_id = eventId
    }
    const response = await api.get('/api/admin/tickets/used', { params })
    return response.data
  },

  async markTicketUsed (id) {
    const response = await api.put(`/api/admin/tickets/${id}/mark-used`)
    return response.data
  },

  async markTicketsUsedByTransaction (transactionId) {
    const response = await api.post(
      '/api/admin/tickets/mark-used-by-transaction',
      {
        transaction_id: transactionId
      }
    )
    return response.data
  },

  async createEvent (event) {
    const response = await api.post('/api/admin/events', event)
    return response.data
  },

  async updateEvent (id, event) {
    const response = await api.put(`/api/admin/events/${id}`, event)
    return response.data
  },

  async deleteEvent (id) {
    await api.delete(`/api/admin/events/${id}`)
  },

  async getUsers (page = 1, limit = 10) {
    const response = await api.get('/api/admin/users', {
      params: { page, limit }
    })
    return response.data
  },

  async getUser (id) {
    const response = await api.get(`/api/admin/users/${id}`)
    return response.data
  },

  async updateAccountStatus (id, status) {
    const response = await api.put(`/api/admin/users/${id}`, status)
    return response.data
  },

  async getPress (page = 1, limit = 10) {
    const response = await api.get('/api/admin/press', {
      params: { page, limit }
    })
    return response.data
  },

  async getPressItem (id) {
    const response = await api.get(`/api/admin/press/${id}`)
    return response.data
  },

  async createPress (press) {
    const response = await api.post('/api/admin/press', press)
    return response.data
  },

  async updatePress (id, press) {
    const response = await api.put(`/api/admin/press/${id}`, press)
    return response.data
  },

  async deletePress (id) {
    await api.delete(`/api/admin/press/${id}`)
  },

  async getCareers (page = 1, limit = 10) {
    const response = await api.get('/api/admin/careers', {
      params: { page, limit }
    })
    return response.data
  },

  async getCareer (id) {
    const response = await api.get(`/api/admin/careers/${id}`)
    return response.data
  },

  async createCareer (career) {
    const response = await api.post('/api/admin/careers', career)
    return response.data
  },

  async updateCareer (id, career) {
    const response = await api.put(`/api/admin/careers/${id}`, career)
    return response.data
  },

  async deleteCareer (id) {
    await api.delete(`/api/admin/careers/${id}`)
  },

  async toggleCareerStatus (id) {
    const response = await api.put(`/api/admin/careers/${id}/toggle`, {})
    return response.data
  },

  async getBanners (page = 1, limit = 10) {
    const response = await api.get('/api/admin/banners', {
      params: { page, limit }
    })
    return response.data
  },

  async getBanner (id) {
    const response = await api.get(`/api/admin/banners/${id}`)
    return response.data
  },

  async createBanner (banner) {
    const response = await api.post('/api/admin/banners', banner)
    return response.data
  },

  async updateBanner (id, banner) {
    const response = await api.put(`/api/admin/banners/${id}`, banner)
    return response.data
  },

  async deleteBanner (id) {
    await api.delete(`/api/admin/banners/${id}`)
  },

  async toggleBannerStatus (id) {
    const response = await api.put(`/api/admin/banners/${id}/toggle`, {})
    return response.data
  },

  async getAnalyticsStats (params = {}) {
    const response = await api.get('/api/admin/analytics/stats', { params })
    return response.data
  },

  async getComprehensiveAnalytics (params = {}) {
    const response = await api.get('/api/admin/analytics/comprehensive', {
      params
    })
    return response.data
  },

  async getTopSearches (params = {}) {
    const response = await api.get('/api/admin/analytics/top-searches', {
      params
    })
    return response.data
  },

  async getTopBookViews (params = {}) {
    const response = await api.get('/api/admin/analytics/top-book-views', {
      params
    })
    return response.data
  },

  async getDailyVisits (params = {}) {
    const response = await api.get('/api/admin/analytics/daily-visits', {
      params
    })
    return response.data
  },

  async recordVisit (visit) {
    const response = await api.post('/api/admin/analytics/visit', visit)
    return response.data
  },

  async getNewsletterSubscribers (page = 1, limit = 10, language = null) {
    const params = { page, limit }
    if (language) {
      params.language = language
    }
    const response = await api.get('/api/admin/newsletter/subscribers', {
      params
    })
    return response.data
  },

  async sendNewsletter (content) {
    const response = await api.post('/api/admin/newsletter/send', content)
    return response.data
  },

  async uploadImage (formData) {
    const response = await uploadApi.post('/api/admin/upload/image', formData)
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data.url
  },

  async uploadEventImage (formData) {
    const response = await uploadApi.post(
      '/api/admin/upload/event-image',
      formData
    )
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data.url
  },

  async uploadPressImage (formData) {
    const response = await uploadApi.post(
      '/api/admin/upload/press-image',
      formData
    )
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data.url
  },

  async uploadBannerImage (formData) {
    const response = await uploadApi.post(
      '/api/admin/upload/banner-image',
      formData
    )
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data.url
  },

  async uploadEbook (formData) {
    const response = await uploadApi.post('/api/admin/upload/ebook', formData)
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data.url
  },

  async uploadDigitalFile (file, category) {
    const formData = new FormData()
    formData.append('file', file)
    if (category) formData.append('category', category)
    const response = await uploadApi.post(
      '/api/admin/upload/digital-file',
      formData
    )
    if (!response.data || !response.data.file_id) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data
  },

  async listDigitalProductsByBook (bookId) {
    const response = await api.get(`/api/admin/books/${bookId}/digital-products`)
    return response.data
  },

  async createDigitalProduct (payload) {
    const response = await api.post('/api/admin/digital-products', payload)
    return response.data
  },

  async updateDigitalProduct (id, payload) {
    const response = await api.put(`/api/admin/digital-products/${id}`, payload)
    return response.data
  },

  async deleteDigitalProduct (id) {
    await api.delete(`/api/admin/digital-products/${id}`)
  },

  async uploadNewsletterVideo (formData) {
    const response = await uploadApi.post(
      '/api/upload/newsletter-video',
      formData
    )
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data.url
  },

  async uploadNewsletterFile (formData) {
    const response = await uploadApi.post(
      '/api/upload/newsletter-file',
      formData
    )
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return response.data
  },

  async getSales (page = 1, limit = 10) {
    const response = await api.get('/api/admin/sales', {
      params: { page, limit }
    })
    return response.data
  },

  async getSale (id) {
    const response = await api.get(`/api/admin/sales/${id}`)
    return response.data
  },

  async getDonations (page = 1, limit = 50, paymentStatus = null) {
    const params = { page, limit }
    if (paymentStatus) params.payment_status = paymentStatus
    const response = await api.get('/api/admin/donations', { params })
    // The donations endpoint returns a plain array.
    return Array.isArray(response.data) ? response.data : []
  },

  async createSale (sale) {
    const response = await api.post('/api/admin/sales', sale)
    return response.data
  },

  async updateSale (id, sale) {
    const response = await api.put(`/api/admin/sales/${id}`, sale)
    return response.data
  },

  async deleteSale (id) {
    await api.delete(`/api/admin/sales/${id}`)
  },

  async getPromotionalTextBanner () {
    const response = await api.get('/api/admin/promotional-text-banner')
    return response.data
  },

  async getPromotionalTextBanners () {
    const response = await api.get('/api/admin/promotional-text-banners')
    return response.data
  },

  async createPromotionalTextBanner (banner) {
    const response = await api.post(
      '/api/admin/promotional-text-banner',
      banner
    )
    return response.data
  },

  async updatePromotionalTextBanner (id, banner) {
    const response = await api.put(
      `/api/admin/promotional-text-banner/${id}`,
      banner
    )
    return response.data
  },

  async togglePromotionalTextBannerStatus (id) {
    const response = await api.put(
      `/api/admin/promotional-text-banner/${id}/toggle`,
      {}
    )
    return response.data
  },

  async deletePromotionalTextBanner (id) {
    await api.delete(`/api/admin/promotional-text-banner/${id}`)
  },

  async getSliderConfig () {
    const response = await api.get('/api/admin/slider-config')
    return response.data
  },

  async updateSliderConfig (config) {
    const response = await api.post('/api/admin/slider-config', config)
    return response.data
  },

  async getBookEmptyMessages () {
    const response = await api.get('/api/admin/book-empty-messages')
    return response.data
  },

  async updateBookEmptyMessages (messages) {
    const response = await api.post('/api/admin/book-empty-messages', {
      messages
    })
    return response.data
  },

  async getSectionConfig (section) {
    const response = await api.get(`/api/admin/section-config/${section}`)
    return response.data
  },

  async updateSectionConfig (section, config) {
    const response = await api.post(
      `/api/admin/section-config/${section}`,
      config
    )
    return response.data
  },

  async getContactSubmissions (page = 1, limit = 20) {
    const response = await api.get('/api/admin/contact', {
      params: { page, limit }
    })
    return response.data
  },

  async getContactSubmission (id) {
    const response = await api.get(`/api/admin/contact/${id}`)
    return response.data
  },

  async deleteContactSubmission (id) {
    await api.delete(`/api/admin/contact/${id}`)
  },

  async getContactInfo () {
    const response = await api.get('/api/admin/contact-info')
    return response.data
  },

  async updateContactInfo (fields) {
    const response = await api.put('/api/admin/contact-info', { fields })
    return response.data
  },

  async getEmailHealth () {
    const response = await api.get('/api/admin/health/email')
    return response.data
  },

  async sendTestEmail (recipient = null) {
    const response = await api.post('/api/admin/health/email/test', { recipient })
    return response.data
  },

  async getManuscriptSubmissions (page = 1, limit = 10) {
    const response = await api.get('/api/admin/manuscripts', {
      params: { page, limit }
    })
    return response.data
  },

  async getManuscriptSubmission (id) {
    const response = await api.get(`/api/admin/manuscripts/${id}`)
    return response.data
  },

  async updateManuscriptSubmission (id, submission) {
    const response = await api.put(`/api/admin/manuscripts/${id}`, submission)
    return response.data
  },

  async deleteManuscriptSubmission (id) {
    await api.delete(`/api/admin/manuscripts/${id}`)
  },

  async getManuscriptSignedUrl (fileUrl) {
    // If the URL is already accessible via the short URL proxy, use it directly
    if (fileUrl && !fileUrl.startsWith('gcs://')) {
      return fileUrl
    }
    // Legacy: gcs:// URL → create a temporary short URL via backend
    try {
      const response = await api.post('/api/admin/upload/private/url', {
        url: fileUrl
      })
      return response.data.url
    } catch (error) {
      console.error('Failed to get signed URL:', error)
      throw error
    }
  },

  async getCareerApplications (page = 1, limit = 20) {
    const response = await api.get('/api/admin/career-applications', {
      params: { page, limit }
    })
    return response.data
  },

  async getCareerApplication (id) {
    const response = await api.get(`/api/admin/career-applications/${id}`)
    return response.data
  },

  async updateCareerApplication (id, application) {
    const response = await api.put(
      `/api/admin/career-applications/${id}`,
      application
    )
    return response.data
  },

  async deleteCareerApplication (id) {
    await api.delete(`/api/admin/career-applications/${id}`)
  },

  async getResumeSignedUrl (fileUrl) {
    // If the URL is already accessible via the short URL proxy, use it directly
    if (fileUrl && !fileUrl.startsWith('gcs://')) {
      return fileUrl
    }
    // Legacy: gcs:// URL → create a temporary short URL via backend
    try {
      const response = await api.post('/api/admin/upload/private/url', {
        url: fileUrl
      })
      return response.data.url
    } catch (error) {
      console.error('Failed to get signed URL:', error)
      throw error
    }
  },

  async getAuthors (params = {}) {
    const response = await api.get('/api/admin/authors', { params })
    return response.data
  },

  async getAuthor (id) {
    const response = await api.get(`/api/admin/authors/${id}`)
    return response.data
  },

  async createAuthor (author) {
    const response = await api.post('/api/admin/authors', author)
    return response.data
  },

  async updateAuthor (id, author) {
    const response = await api.put(`/api/admin/authors/${id}`, author)
    return response.data
  },

  async deleteAuthor (id) {
    await api.delete(`/api/admin/authors/${id}`)
  },

  async getAuthorBooks (id) {
    const response = await api.get(`/api/admin/authors/${id}/books`)
    return response.data
  },

  async linkBookToAuthor (bookId, authorId) {
    const response = await api.post('/api/admin/authors/link-book', {
      book_id: bookId,
      author_id: authorId
    })
    return response.data
  },

  async unlinkBookFromAuthor (bookId, authorId) {
    const response = await api.post('/api/admin/authors/unlink-book', {
      book_id: bookId,
      author_id: authorId
    })
    return response.data
  },

  async getBookAuthors () {
    const response = await api.get('/api/admin/authors/book-authors')
    return response.data
  },

  async createAuthorFromBook (authorData) {
    const response = await api.post(
      '/api/admin/authors/create-from-book',
      authorData
    )
    return response.data
  },

  async mergeAuthors (sourceAuthorIds, targetAuthorId) {
    const response = await api.post('/api/admin/authors/merge', {
      source_author_ids: sourceAuthorIds,
      target_author_id: targetAuthorId
    })
    return response.data
  },

  // Categories
  async getCategories (params = {}) {
    const response = await api.get('/api/admin/categories', { params })
    return response.data
  },

  async getCategory (id) {
    const response = await api.get(`/api/admin/categories/${id}`)
    return response.data
  },

  async getCategoryBySlug (slug) {
    const response = await api.get(`/api/admin/categories/slug/${slug}`)
    return response.data
  },

  async createCategory (category) {
    const response = await api.post('/api/admin/categories', category)
    return response.data
  },

  async updateCategory (id, category) {
    const response = await api.put(`/api/admin/categories/${id}`, category)
    return response.data
  },

  async deleteCategory (id) {
    await api.delete(`/api/admin/categories/${id}`)
  },

  async getBookCategories (bookId) {
    const response = await api.get(`/api/admin/categories/book/${bookId}`)
    return response.data
  },

  async linkBookToCategory (bookId, categoryId) {
    const response = await api.post('/api/admin/categories/link-book', {
      book_id: bookId,
      category_id: categoryId
    })
    return response.data
  },

  async unlinkBookFromCategory (bookId, categoryId) {
    const response = await api.post('/api/admin/categories/unlink-book', {
      book_id: bookId,
      category_id: categoryId
    })
    return response.data
  },

  async getPages (page = 1, limit = 100) {
    const response = await api.get('/api/admin/pages', {
      params: { page, limit }
    })
    return response.data
  },

  async getPage (id) {
    const response = await api.get(`/api/admin/pages/${id}`)
    return response.data
  },

  async createPage (page) {
    const response = await api.post('/api/admin/pages', page)
    return response.data
  },

  async updatePage (id, page) {
    const response = await api.put(`/api/admin/pages/${id}`, page)
    return response.data
  },

  async deletePage (id) {
    await api.delete(`/api/admin/pages/${id}`)
  },

  async getTeamMembers (page = 1, limit = 100) {
    const response = await api.get('/api/admin/team-members', {
      params: { page, limit }
    })
    return response.data
  },

  async getTeamMember (id) {
    const response = await api.get(`/api/admin/team-members/${id}`)
    return response.data
  },

  async createTeamMember (member) {
    const response = await api.post('/api/admin/team-members', member)
    return response.data
  },

  async updateTeamMember (id, member) {
    const response = await api.put(`/api/admin/team-members/${id}`, member)
    return response.data
  },

  async deleteTeamMember (id) {
    await api.delete(`/api/admin/team-members/${id}`)
  },

  async getPartners (page = 1, limit = 100) {
    const response = await api.get('/api/admin/partners', {
      params: { page, limit }
    })
    return response.data
  },

  async getPartner (id) {
    const response = await api.get(`/api/admin/partners/${id}`)
    return response.data
  },

  async createPartner (partner) {
    const response = await api.post('/api/admin/partners', partner)
    return response.data
  },

  async updatePartner (id, partner) {
    const response = await api.put(`/api/admin/partners/${id}`, partner)
    return response.data
  },

  async deletePartner (id) {
    await api.delete(`/api/admin/partners/${id}`)
  },

  async getDeliveryOptions (page = 1, limit = 100) {
    const response = await api.get('/api/admin/delivery-options', {
      params: { page, limit }
    })
    return response.data
  },

  async getDeliveryOption (id) {
    const response = await api.get(`/api/admin/delivery-options/${id}`)
    return response.data
  },

  async createDeliveryOption (option) {
    const response = await api.post('/api/admin/delivery-options', option)
    return response.data
  },

  async updateDeliveryOption (id, option) {
    const response = await api.put(`/api/admin/delivery-options/${id}`, option)
    return response.data
  },

  async deleteDeliveryOption (id) {
    await api.delete(`/api/admin/delivery-options/${id}`)
  },

  async getFaqs (page = 1, limit = 100) {
    const response = await api.get('/api/admin/faqs', {
      params: { page, limit }
    })
    return response.data
  },

  async getFaq (id) {
    const response = await api.get(`/api/admin/faqs/${id}`)
    return response.data
  },

  async createFaq (faq) {
    const response = await api.post('/api/admin/faqs', faq)
    return response.data
  },

  async updateFaq (id, faq) {
    const response = await api.put(`/api/admin/faqs/${id}`, faq)
    return response.data
  },

  async deleteFaq (id) {
    await api.delete(`/api/admin/faqs/${id}`)
  },

  async uploadGuidelinesFile (file) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await uploadApi.post(
      '/api/admin/upload/guidelines',
      formData
    )
    if (!response.data || !response.data.url) {
      throw new Error(
        'Invalid response from server: ' + JSON.stringify(response.data)
      )
    }
    return { success: true, url: response.data.url }
  },

  async getStripeReceipt (paymentIntentId) {
    const response = await api.get(
      `/api/admin/test/stripe-receipt/${encodeURIComponent(paymentIntentId)}`
    )
    return response.data
  },

  async compareOrder (saleId) {
    const response = await api.get(`/api/admin/test/order-compare/${saleId}`)
    return response.data
  }
}
